import SEO from "@/components/SEO";
import { PageHero, SectionHead, CTABand, Reveal } from "@/components/Ui";
import { ShieldCheck, Handshake, Wrench, HeartHandshake } from "lucide-react";

const valeurs = [
  { icon: ShieldCheck, t: "Le sérieux", d: "Un chantier préparé, tenu et livré comme convenu." },
  { icon: Wrench, t: "Le savoir-faire", d: "Dix ans de métier, du gros œuvre aux finitions." },
  { icon: Handshake, t: "La proximité", d: "Une entreprise de la région de Bernay, disponible et réactive." },
  { icon: HeartHandshake, t: "L'accompagnement", d: "Des conseils clairs et un suivi du premier appel à la réception." },
];

export default function APropos() {
  return (
    <>
      <SEO
        title="À propos de DMZ Normandie | Entreprise de maçonnerie près de Bernay"
        description="DMZ Normandie : entreprise de maçonnerie, gros œuvre et rénovation près de Bernay (Eure). Sérieux, qualité de travail et accompagnement personnalisé."
        path="/a-propos"
      />
      <PageHero
        title="À propos de DMZ Normandie"
        subtitle="Une entreprise de maçonnerie à taille humaine, au service des particuliers et des professionnels de la région de Bernay."
      />

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <SectionHead overline="Qui sommes-nous" title="Un artisan du bâtiment, pas un discours" />
            <div className="-mt-6 space-y-5 text-zinc-400 font-light leading-relaxed">
              <p>
                DMZ Normandie est une entreprise spécialisée dans la maçonnerie, la rénovation,
                le gros œuvre, la construction et l'aménagement extérieur. Depuis dix ans, nous
                construisons et réparons dans la région de Bernay, avec plus de 150 chantiers réalisés.
              </p>
              <p>
                Notre façon de travailler est simple : écouter votre projet, vous conseiller
                honnêtement, chiffrer clairement et réaliser proprement. Nous préférons un mur
                bien monté à un beau discours — et nos clients nous rappellent pour cela.
              </p>
              <p>
                Parpaing, brique, biobrique ou béton : nous travaillons les matériaux adaptés à
                votre projet et à votre budget, sans compromis sur la solidité.
              </p>
              <p>
                Notre zone de travail s'étend de Bernay à Évreux et couvre l'ensemble de l'Eure.
                Selon la nature et l'importance du chantier, nous intervenons aussi ailleurs en
                Normandie. Que vous soyez particulier ou professionnel, vous échangez directement
                avec celui qui conduit vos travaux — sans intermédiaire ni mauvaise surprise.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative">
              <div className="absolute -top-4 -right-4 w-full h-full border border-[#C4A484]" aria-hidden="true" />
              <img
                src="/realisations/facade-brique-silex.webp"
                alt="Maçonnerie traditionnelle brique et silex rénovée par DMZ Normandie"
                className="relative w-full h-[420px] object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {valeurs.map((v, i) => (
            <Reveal key={v.t} delay={i * 0.06} className="card-dark p-8">
              <v.icon size={22} className="text-[#C4A484] mb-5" />
              <h3 className="font-medium text-[#F5F5F0] mb-3">{v.t}</h3>
              <p className="text-sm font-light text-zinc-500 leading-relaxed">{v.d}</p>
            </Reveal>
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-16 grid grid-cols-3 gap-6 border-t border-white/10 pt-12" data-testid="about-stats">
          {[
            { v: "10", l: "années de métier" },
            { v: "150", l: "chantiers livrés" },
            { v: "5+", l: "départements couverts" },
          ].map((s) => (
            <Reveal key={s.l} className="text-center">
              <p className="font-serif-display font-light text-4xl md:text-5xl text-[#C4A484]">{s.v}</p>
              <p className="mt-2 text-[10px] md:text-xs tracking-[0.2em] uppercase text-zinc-500">{s.l}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <CTABand />
    </>
  );
}
